import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { getCoverImageUrl } from '../utils/musicStreamUrls';

/**
 * SEO 组件 - 用于管理页面的 SEO 元标签
 * 
 * @param {Object} props
 * @param {string} props.title - 页面标题
 * @param {string} props.description - 页面描述
 * @param {string} props.image - 页面图片 URL（用于 Open Graph）
 * @param {string} props.url - 页面 URL（用于 canonical 和 Open Graph）
 * @param {string} props.type - Open Graph 类型（website, article, music.song 等）
 * @param {Object} props.music - 音乐对象（用于音乐详情页的结构化数据）
 * @param {Array} props.hreflang - hreflang 链接数组 [{lang: 'ja', url: '/ja/...'}, ...]
 */
const SEO = ({
  title,
  description,
  image,
  url,
  type = 'website',
  music = null,
  hreflang = []
}) => {
  const { i18n } = useTranslation();
  const currentLang = i18n?.language || 'ja';
  
  // 网站基础信息
  const siteName = '天流音楽 - tianliu.ai';
  const siteUrl = typeof window !== 'undefined' 
    ? `${window.location.protocol}//${window.location.host}`
    : 'https://tianliu.ai';
  
  // 默认值
  const defaultTitle = '天流音楽 - わたしは天流れ、わたしは命';
  const defaultDescription = '天流音楽 - 多语言音乐平台，提供中文、日文、英文及世界音乐。天流音楽，わたしは天流れ、わたしは命。';
  const defaultImage = `${siteUrl}/favicon512.png`;
  
  const finalTitle = title || defaultTitle;
  const finalDescription = description || defaultDescription;
  const finalImage = image || defaultImage;
  const finalUrl = url || (typeof window !== 'undefined' ? window.location.href : siteUrl);
  
  // 生成 hreflang 标签
  const hreflangTags = hreflang.length > 0 
    ? hreflang 
    : [
        { lang: 'ja', url: `${siteUrl}/ja` },
        { lang: 'cn', url: `${siteUrl}/cn` },
        { lang: 'tw', url: `${siteUrl}/tw` },
        { lang: 'en', url: `${siteUrl}/en` },
        { lang: 'x-default', url: `${siteUrl}/ja` }
      ];
  
  // 生成音乐结构化数据（JSON-LD）
  const musicCoverForSeo = music ? (getCoverImageUrl(music) || null) : null;
  const musicStructuredData = music ? {
    '@context': 'https://schema.org',
    '@type': 'MusicRecording',
    name: music.title || '',
    byArtist: {
      '@type': 'MusicGroup',
      name: music.artist || '天流音楽'
    },
    duration: music.duration ? `PT${Math.floor(music.duration)}S` : undefined,
    image: musicCoverForSeo || finalImage,
    url: finalUrl,
    ...(music.description && { description: music.description }),
    ...(music.album && { inAlbum: { '@type': 'MusicAlbum', name: music.album } })
  } : null;
  
  // 网站结构化数据
  const websiteStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: siteName,
    url: siteUrl,
    description: defaultDescription,
    inLanguage: ['ja', 'cn', 'tw', 'en'],
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${siteUrl}/search?q={search_term_string}`
      },
      'query-input': 'required name=search_term_string'
    }
  };
  
  return (
    <Helmet>
      {/* 基础 SEO */}
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <link rel="canonical" href={finalUrl} />
      
      {/* 语言标签 */}
      <html lang={currentLang} />
      
      {/* hreflang 标签 - 多语言支持 */}
      {hreflangTags.map((item, index) => (
        <link
          key={index}
          rel="alternate"
          hrefLang={item.lang}
          href={item.url}
        />
      ))}
      
      {/* Open Graph 标签 - Facebook, LinkedIn 等 */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:image" content={finalImage} />
      <meta property="og:url" content={finalUrl} />
      <meta property="og:site_name" content={siteName} />
      <meta
        property="og:locale"
        content={currentLang === 'ja' ? 'ja_JP' : currentLang === 'cn' ? 'zh_CN' : currentLang === 'tw' ? 'zh_TW' : 'en_US'}
      />
      
      {/* 音乐相关的 Open Graph */}
      {music && (
        <>
          <meta property="og:audio" content={music.audio_url || ''} />
          {music.artist && <meta property="music:musician" content={music.artist} />}
          {music.duration && <meta property="music:duration" content={Math.floor(music.duration)} />}
        </>
      )}
      
      {/* Twitter Card 标签 */}
      <meta name="twitter:card" content={music ? "player" : "summary_large_image"} />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={finalImage} />
      {music && music.audio_url && (
        <meta name="twitter:player" content={music.audio_url} />
      )}
      
      {/* 结构化数据 - JSON-LD */}
      {musicStructuredData && (
        <script type="application/ld+json">
          {JSON.stringify(musicStructuredData)}
        </script>
      )}
      <script type="application/ld+json">
        {JSON.stringify(websiteStructuredData)}
      </script>
    </Helmet>
  );
};

export default SEO;
